import { useState, useEffect } from "react";
import { FiX } from "react-icons/fi";

export default function WelcomeMessageModal({
  isOpen,
  onClose,
  onSave,
  currentMessage,
  getTimeBasedGreeting,
}) {
  const [selectedOption, setSelectedOption] = useState("time-based");
  const [customMessage, setCustomMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      // Check if current message is time-based, custom, or one of the preset options
      const timeBasedGreeting = getTimeBasedGreeting();
      if (currentMessage === timeBasedGreeting) {
        setSelectedOption("time-based");
      } else if (currentMessage && currentMessage !== timeBasedGreeting) {
        // Check if it's a preset option
        const presetOptions = [
          "Welcome! How can I assist you today?",
          "Hello! What would you like to know?",
        ];
        if (presetOptions.includes(currentMessage)) {
          setSelectedOption(
            currentMessage === presetOptions[0] ? "option1" : "option2"
          );
        } else {
          setSelectedOption("custom");
          setCustomMessage(currentMessage);
        }
      }
    }
  }, [isOpen, currentMessage, getTimeBasedGreeting]);

  const handleSave = () => {
    if (selectedOption === "time-based") {
      onSave("", true); // Pass empty message and isTimeBased flag
      onClose();
    } else {
      let messageToSave = "";
      if (selectedOption === "option1") {
        messageToSave = "Welcome! How can I assist you today?";
      } else if (selectedOption === "option2") {
        messageToSave = "Hello! What would you like to know?";
      } else if (selectedOption === "custom") {
        messageToSave = customMessage.trim();
      }

      if (messageToSave) {
        onSave(messageToSave, false);
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  const timeBasedGreeting = getTimeBasedGreeting();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-bg-dark dark:border dark:border-gray-200 rounded-lg max-w-md w-full mx-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          <FiX size={24} />
        </button>

        {/* Content */}
        <div className="p-6 pt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Select Welcome Message
          </h2>

          {/* Options */}
          <div className="space-y-4 mb-6">
            {/* Time-based option */}
            <label className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <input
                type="radio"
                name="welcomeMessage"
                value="time-based"
                checked={selectedOption === "time-based"}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="mt-1 w-4 h-4 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">
                  Time-based greeting
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {timeBasedGreeting}
                </div>
              </div>
            </label>

            {/* Option 1 */}
            <label className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <input
                type="radio"
                name="welcomeMessage"
                value="option1"
                checked={selectedOption === "option1"}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="mt-1 w-4 h-4 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">
                  Welcome! How can I assist you today?
                </div>
              </div>
            </label>

            {/* Option 2 */}
            <label className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <input
                type="radio"
                name="welcomeMessage"
                value="option2"
                checked={selectedOption === "option2"}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="mt-1 w-4 h-4 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">
                  Hello! What would you like to know?
                </div>
              </div>
            </label>

            {/* Custom option */}
            <label className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <input
                type="radio"
                name="welcomeMessage"
                value="custom"
                checked={selectedOption === "custom"}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="mt-1 w-4 h-4 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white mb-2">
                  Custom message
                </div>
                <input
                  type="text"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOption("custom");
                  }}
                  placeholder="Enter your custom welcome message..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-bg-light dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={
                selectedOption === "custom" && !customMessage.trim()
              }
              className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
                selectedOption === "custom" && !customMessage.trim()
                  ? "bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed"
                  : "bg-indigo-900 dark:bg-indigo-700 text-white hover:bg-indigo-800 dark:hover:bg-indigo-600"
              }`}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

